import {
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { CmpsFilterComponent, CmpsFilterSelectionItem } from '@cmps/ng-uikit';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AuthService } from '../../../auth/auth.service';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { StateToProps } from '../../../store';
import { disuseStore, useDispatcher, useStore } from '../../../store.decorator';
import { ScorecardDataService } from '../../core/data.service';
import { ScorecardGridOptions } from '../../core/scorecard-grid-options.model';
import {
    GridType,
    Widget,
    WidgetType,
} from '../../core/template-definition.model';
import { TranslationKeyMap } from '../../scorecard.i18n';
import {
    CMPS_SCORECARD_INITIAL_GRID_TYPE,
    CMPS_SCORECARD_PAGE,
    CMPS_SCORECARD_WIDGET,
} from '../../scorecard.metadata';
import { GridData } from '../../scorecard.model';

import { DirectReportContributionGridComponent } from './direct-report-contribution-grid.component';
import { DirectReportrankGridComponent } from './direct-report-rank-grid.component';
import { DirectReportScorecardGridComponent } from './direct-report-scorecard-grid.component';
import { FilterCategory } from './filter-category.model';
import {
    ScoredardBridgeService,
    TeamgridComponentComm,
} from './sc-bridge.service';
import {
    contributionGridReset,
    contributionReload,
    drScorecardGridReset,
    expandAll,
    expandAllScorecard,
    expandMetricColumns,
    expandScorecardMetricColumns,
    filterDirectReportContributionGrid,
    filterDirectReportContributionGridPillar,
    filterDirectReportRankGrid,
    filterDirectReportScorecardGrid,
    filterDirectReportScorecardGridPillar,
    rankGridReset,
    rankReload,
    scorecardReload,
    sortDirectReportContributionGrid,
    sortDirectReportRankGrid,
    sortDirectReportScorecardGrid,
} from './team-grid.action';
import { FilterPillar, ScorecardSelection } from './team-grid.model';
import teamGridReducer from './team-grid.reducer';
import {
    DirectReportContributionGrid,
    DirectReportRankGrid,
    DirectReportScorecardGrid,
    TeamGridState,
} from './team-grid.state';

@Component({
    selector: 'cmps-team-grid',
    templateUrl: './team-grid.component.html',
})
@TakeUntilDestroy()
export class TeamGridComponent
    implements
        OnInit,
        OnDestroy,
        StateToProps<TeamGridState>,
        TeamgridComponentComm
{
    @ViewChild('contribution')
    private readonly _contribution: DirectReportContributionGridComponent;

    @ViewChild('SCView')
    private readonly _scView: DirectReportScorecardGridComponent;

    @ViewChild('RankView')
    private readonly _rankView: DirectReportrankGridComponent;

    state: TeamGridState;
    activeTab: GridType;
    _prevData: GridData;
    @Input() model: any;
    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _activeGrid: GridType;
    isExpandableGrid: boolean;
    activeGridExpandedAll: boolean;
    private readonly _activeGridExpandedAllSubj: BehaviorSubject<boolean>;
    /** @private */ _displayPillarFilter: boolean;
    /** @private */ _activeGridModel:
        | DirectReportContributionGrid
        | DirectReportScorecardGrid
        | DirectReportRankGrid;

    /**
     * @private
     * this is a component level copy of the one in the redux state
     */
    _filterPillarList: FilterPillar[];

    /** @private */ _onFilterSelectionCopy: FilterPillar[];
    /** @private */ _filterCategories: Array<[FilterCategory, string, boolean]>;
    @ViewChild('filter')
    private readonly _filter: CmpsFilterComponent<FilterPillar>;

    public slideBarColour: string;

    constructor(
        @Inject(CMPS_SCORECARD_WIDGET) /** @private */
        public _widget: Widget<ScorecardGridOptions>,
        @Inject(CMPS_SCORECARD_PAGE) private readonly _page: number,
        @Inject(CMPS_SCORECARD_INITIAL_GRID_TYPE)
        private readonly _initialGridType: () => GridType,
        private readonly _dataService: ScorecardDataService,
        private readonly _authService: AuthService,
        private readonly _scBridge: ScoredardBridgeService,
    ) {
        this.slideBarColour = _widget.slideBarColour;
        this.activeTab = _initialGridType();
        this._activeGridExpandedAllSubj = new BehaviorSubject(false);
        this._filterCategories = [
            [FilterCategory.PILLAR, TranslationKeyMap._9255_PILLAR, true],
            [
                FilterCategory.SUPER_GROUP,
                TranslationKeyMap._67_SUPERGROUP,
                true,
            ],
        ];
    }

    ngOnInit(): void {
        // TODO revise, refactor. this is temporary in order to separate the grids
        // TODO refactor. each grid should be changed to be a WidgetType
        useStore<TeamGridState>(
            this,
            `scorecard.widgets.team-grid.page${this._page}`,
            teamGridReducer.teamGrid,
        );

        this._dataService
            .observableForWidgetType<GridData>(WidgetType.SCORECARD_GRID)
            .pipe(
                tap((data) => {
                    this._prevData = data;
                    this.reset();
                    useDispatcher(
                        this,
                        contributionReload({
                            ...data,
                            options: this._widget.options,
                        }),
                    );
                    useDispatcher(
                        this,
                        rankReload({
                            ...data,
                            options: this._widget.options,
                        }),
                    );
                    useDispatcher(
                        this,
                        scorecardReload({
                            ...data,
                            options: this._widget.options,
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
        this._scBridge.componentTeamgrid.connect(this);
    }

    ngOnDestroy(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._activeGridExpandedAllSubj &&
            this._activeGridExpandedAllSubj.complete();

        this._scBridge.componentTeamgrid.disconnect();

        disuseStore(this);
    }

    stateToProps(prevState?: TeamGridState): void {
        this._checkActiveGridExpandState();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._activeGridExpandedAllSubj &&
            this._activeGridExpandedAllSubj.next(this.activeGridExpandedAll);
    }

    layout(): void {
        switch (this._activeGrid) {
            case GridType.Rank_View:
                this._rankView.layout();
                break;
            case GridType.Contribution_View:
                this._contribution.layout();
                break;
            case GridType.Direct_Reports_View:
                this._scView.layout();
                break;
        }
    }

    onTabChange(evt: GridType): void {
        this.activeTab = evt;
        this._checkActiveGridExpandState();
    }

    expandAllChange(): Observable<boolean> {
        return this._activeGridExpandedAllSubj.asObservable();
    }

    willBeHidden(): void {
        this._filter?.overlay?.toggle();
    }

    /** @private */ _onGridSelect(gridType: GridType): void {
        this._activeGrid = gridType;
        this._checkActiveGridExpandState();
        this._activeGridExpandedAllSubj.next(this.activeGridExpandedAll);
    }

    /** @private */ _onDirectReportRankSortChange(columnId: string): void {
        useDispatcher(this, sortDirectReportRankGrid({ columnId }));
    }

    /** @private */ _onExpandClick(columnId: string): void {
        useDispatcher(this, expandMetricColumns({ columnId }));
        this._contribution.layout();
    }

    /** @private */ _onExpandScorecardClick(columnId: string): void {
        useDispatcher(this, expandScorecardMetricColumns({ columnId }));
        this._scView.layout();
    }

    /** @private */ _onDirectReportContributionSortChange(
        columnId: string,
    ): void {
        useDispatcher(this, sortDirectReportContributionGrid({ columnId }));
    }

    /** @private */ _onDirectReportScorecardSortChange(columnId: string): void {
        useDispatcher(this, sortDirectReportScorecardGrid({ columnId }));
    }

    /** @private */ _onScorecardDRSelect(
        scSelection: ScorecardSelection,
    ): void {
        this._scBridge.componentScorecard.bridgeCreateTab(scSelection);
    }

    reset(): void {
        this._displayPillarFilter = false;
        this._onPillarFilterClearClick(null);

        switch (this._activeGrid) {
            case GridType.Rank_View:
                useDispatcher(this, rankGridReset({}));
                break;
            case GridType.Contribution_View:
                useDispatcher(this, contributionGridReset({}));
                useDispatcher(this, expandAll({ expand: false }));
                break;
            case GridType.Direct_Reports_View:
                useDispatcher(this, drScorecardGridReset({}));
                useDispatcher(this, expandAllScorecard({ expand: false }));
                break;
            default:
                break;
        }
    }

    toggleExpandAll(): void {
        switch (this._activeGrid) {
            case GridType.Contribution_View:
                useDispatcher(
                    this,
                    expandAll({ expand: !this.activeGridExpandedAll }),
                );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._contribution && this._contribution.layout();
                break;
            case GridType.Direct_Reports_View:
                useDispatcher(
                    this,
                    expandAllScorecard({ expand: !this.activeGridExpandedAll }),
                );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._scView && this._scView.layout();
                break;
        }
    }

    /** @private */ _toggleDisplayPillarFilterClick(
        $event: MouseEvent | KeyboardEvent,
    ): void {
        $event.stopPropagation();
        this._displayPillarFilter = !this._displayPillarFilter;
        this._onFilterSelectionCopy = JSON.parse(
            JSON.stringify(this._filterPillarList),
        );
        if (this.activeTab === GridType.Rank_View) {
            this._filterCategories[0][2] = false;
        } else {
            // eslint-disable-next-line no-return-assign
            this._filterCategories.forEach((x) => (x[2] = true));
        }
    }

    /** @private */ _onSelectionClick(filterPillar: FilterPillar): void {
        if (filterPillar.category === FilterCategory.PILLAR) {
            const foundItem = this._filterPillarList.find(
                (x) =>
                    x.columnId === filterPillar.columnId &&
                    x.category === FilterCategory.PILLAR,
            );
            if (foundItem) {
                foundItem.isSelected = false;
                foundItem.disabled = false;
            }

            const noneSelected = !this._filterPillarList.some(
                (x) => x.isSelected && x.category === FilterCategory.PILLAR,
            );
            this._filterPillars(
                noneSelected
                    ? null
                    : this._filterPillarList.filter(
                          (e) => e.category === FilterCategory.PILLAR,
                      ),
            );
            const isSupergrouApplied = this._onFilterSelectionCopy.find(
                (y) =>
                    y.category === FilterCategory.SUPER_GROUP &&
                    y.isSelected === true,
            );
            if (isSupergrouApplied) {
                this._filterSuperGroups(
                    this._filterPillarList.filter(
                        (e) => e.category === FilterCategory.SUPER_GROUP,
                    ),
                );
            }
        }

        if (filterPillar.category === FilterCategory.SUPER_GROUP) {
            const foundSGItem = this._filterPillarList.find(
                (x) =>
                    x.value === filterPillar.value &&
                    x.category === FilterCategory.SUPER_GROUP,
            );
            if (foundSGItem) {
                foundSGItem.isSelected = false;
                foundSGItem.disabled = false;
            }

            const noneSelected = !this._filterPillarList.some(
                (x) =>
                    x.isSelected && x.category === FilterCategory.SUPER_GROUP,
            );
            this._filterSuperGroups(
                noneSelected
                    ? null
                    : this._filterPillarList.filter(
                          (e) => e.category === FilterCategory.SUPER_GROUP,
                      ),
            );
        }
    }

    /** @private */ _onFilterOptionItem(
        $event: CmpsFilterSelectionItem<FilterPillar>,
    ): void {
        if ($event.item.category === FilterCategory.PILLAR) {
            const foundItem = this._onFilterSelectionCopy.find(
                (x) =>
                    x.columnId === $event.item.columnId &&
                    x.category === FilterCategory.PILLAR,
            );
            if (foundItem) {
                foundItem.isSelected = !foundItem.isSelected;
                foundItem.disabled = false;
            }
        }

        if ($event.item.category === FilterCategory.SUPER_GROUP) {
            const foundItemSG = this._onFilterSelectionCopy.find(
                (x) =>
                    x.label === $event.item.label &&
                    x.category === FilterCategory.SUPER_GROUP,
            );
            if (foundItemSG) {
                foundItemSG.isSelected = !foundItemSG.isSelected;
                foundItemSG.disabled = false;
            }
        }
    }

    /** @private */ _onApplyFilterOptionClick(): void {
        const selectedPillars = this._onFilterSelectionCopy.filter(
            (x) =>
                x.isSelected === true && x.category === FilterCategory.PILLAR,
        );
        this._filterPillars(
            selectedPillars.length === 0
                ? null
                : this._onFilterSelectionCopy.filter(
                      (e) => e.category === FilterCategory.PILLAR,
                  ),
        );

        const selectedSuperGroups = this._onFilterSelectionCopy.filter(
            (x) =>
                x.isSelected === true &&
                x.category === FilterCategory.SUPER_GROUP,
        );
        this._filterSuperGroups(
            selectedSuperGroups.length === 0
                ? null
                : this._onFilterSelectionCopy.filter(
                      (e) => e.category === FilterCategory.SUPER_GROUP,
                  ),
        );
    }

    /** @private */ _onPillarFilterClearClick(
        $event: MouseEvent | KeyboardEvent,
    ): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        $event && $event.stopPropagation();
        this._displayPillarFilter = false;
        this._filterPillars(null);
    }

    private _filterPillars(payload: FilterPillar[]): void {
        switch (this._activeGrid) {
            case GridType.Contribution_View:
                useDispatcher(
                    this,
                    filterDirectReportContributionGridPillar({ payload }),
                );
                break;
            case GridType.Direct_Reports_View:
                useDispatcher(
                    this,
                    filterDirectReportScorecardGridPillar({ payload }),
                );
                break;
        }
    }

    private _filterSuperGroups(payload: FilterPillar[]): void {
        switch (this._activeGrid) {
            case GridType.Rank_View:
                useDispatcher(this, filterDirectReportRankGrid({ payload }));
                break;
            case GridType.Contribution_View:
                useDispatcher(
                    this,
                    filterDirectReportContributionGrid({ payload }),
                );
                break;
            case GridType.Direct_Reports_View:
                useDispatcher(
                    this,
                    filterDirectReportScorecardGrid({ payload }),
                );
                break;
        }
    }

    private _checkActiveGridExpandState(): void {
        switch (this._activeGrid) {
            case GridType.Rank_View:
                this._activeGridModel = this.state.directReportRankGrid;
                this._filterPillarList =
                    this.state.directReportRankGrid.filterDef.map((item) => {
                        return {
                            name: '',
                            isSelected: item.isSelected,
                            columnId: '',
                            value: item.value,
                            label: item.label,
                            disabled: item.disabled,
                            category: FilterCategory.SUPER_GROUP,
                        };
                    });
                break;
            case GridType.Contribution_View:
                this._activeGridModel = this.state.directReportContributionGrid;
                this._filterPillarList =
                    this.state.directReportContributionGrid.filterPillar.map(
                        (item) => {
                            return {
                                name: item.name,
                                isSelected: item.isSelected,
                                columnId: item.columnId,
                                value: '',
                                label: item.name,
                                disabled: false,
                                category: FilterCategory.PILLAR,
                            };
                        },
                    );
                // eslint-disable-next-line no-case-declarations
                const filterDefList1 =
                    this.state.directReportContributionGrid.filterDef.map(
                        (item) => {
                            return {
                                name: '',
                                isSelected: item.isSelected,
                                columnId: '',
                                value: item.value,
                                label: item.label,
                                disabled: item.disabled,
                                category: FilterCategory.SUPER_GROUP,
                            };
                        },
                    );

                this._filterPillarList.push(...filterDefList1);

                this.isExpandableGrid = true;
                this.activeGridExpandedAll =
                    this.state.directReportContributionGridExpandedAll;
                break;
            case GridType.Direct_Reports_View:
                this._activeGridModel = this.state.directReportScorecardGrid;

                this._filterPillarList =
                    this.state.directReportScorecardGrid.filterPillar.map(
                        (item) => {
                            return {
                                name: item.name,
                                isSelected: item.isSelected,
                                columnId: item.columnId,
                                value: '',
                                label: item.name,
                                disabled: false,
                                category: FilterCategory.PILLAR,
                            };
                        },
                    );
                // eslint-disable-next-line no-case-declarations
                const filterDefList2 =
                    this.state.directReportScorecardGrid.filterDef.map(
                        (item) => {
                            return {
                                name: '',
                                isSelected: item.isSelected,
                                columnId: '',
                                value: item.value,
                                label: item.label,
                                disabled: item.disabled,
                                category: FilterCategory.SUPER_GROUP,
                            };
                        },
                    );
                this._filterPillarList.push(...filterDefList2);

                this.isExpandableGrid = true;
                this.activeGridExpandedAll =
                    this.state.directReportScorecardGridExpandedAll;
                break;
            default:
                this._activeGridModel = null;
                this.isExpandableGrid = false;
                this.activeGridExpandedAll = false;
        }
    }
}
